import React from "react";
import Section from "./Section";

export default function HTML_Elements() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🏗️ HTML Elements — Complete Guide</h1>

      <p className="tutorial-intro">
        HTML <strong>elements</strong> are the building blocks of every web
        page. Each element tells the browser how to display content — like text,
        images, links, and sections. In this lesson, we’ll explore element
        structure, nesting, and semantic meaning.
      </p>

      {/* 🔹 Section 1 — What is an HTML Element? */}
      <Section
        title="1️⃣ What is an HTML Element?"
        html={`<p>This is a paragraph element.</p>
<h1>This is a heading element</h1>
<a href="https://example.com">This is a link element</a>`}
        description="An HTML element usually consists of an opening tag, content, and a closing tag. For example: <p>Content</p>."
      />

      <div className="Key_point_container">
        <ul className="tutorial-list-points">
          <li>
            <b>Opening tag:</b> <code>&lt;p&gt;</code>
          </li>
          <li>
            <b>Content:</b> The text or inner HTML
          </li>
          <li>
            <b>Closing tag:</b> <code>&lt;/p&gt;</code>
          </li>
        </ul>
      </div>

      {/* 🔹 Section 2 — Nested Elements */}
      <Section
        title="2️⃣ Nested Elements"
        html={`<div>
  <h2>Nested Example</h2>
  <p>This paragraph contains a <strong>bold</strong> word and an <em>italic</em> word.</p>
</div>`}
        description="HTML elements can be nested inside one another. Make sure each nested tag is properly closed in reverse order."
      />

      <div className="Key_point_container">
        <ul className="tutorial-list-points">
          <li>
            Elements can be placed inside other elements — this is called
            <b> nesting</b>.
          </li>
          <li>
            Always close tags properly to maintain a valid document structure.
          </li>
        </ul>
      </div>

      {/* 🔹 Section 3 — Empty Elements */}
      <Section
        title="3️⃣ Empty Elements"
        html={`<img src="https://via.placeholder.com/100" alt="Sample Image" />
<br />
<hr />
<input type="text" placeholder="Enter text here" />`}
        description="Some HTML elements have no closing tag or content inside — they are called empty or self-closing elements."
      />

      <ul className="tutorial-list-points">
        <li>
          <code>&lt;img /&gt;</code> — for images
        </li>
        <li>
          <code>&lt;br /&gt;</code> — for line breaks
        </li>
        <li>
          <code>&lt;hr /&gt;</code> — for horizontal lines
        </li>
        <li>
          <code>&lt;input /&gt;</code> — for form fields
        </li>
      </ul>

      {/* 🔹 Section 4 — Block vs Inline Elements */}
      <Section
        title="4️⃣ Block vs Inline Elements"
        html={`<div style="border:1px solid #ccc; padding:8px;">
  <h3>Block Elements</h3>
  <p>Paragraphs, divs, and lists are block-level elements.</p>
</div>

<span style="color:blue;">Inline text</span>
<strong>Bold inline text</strong>`}
        description="HTML elements can be block-level (start on a new line and take full width) or inline (sit within a line of text)."
      />

      <ul className="tutorial-list-points">
        <li>
          <b>Block elements:</b> div, p, h1–h6, section, article
        </li>
        <li>
          <b>Inline elements:</b> span, strong, em, a, img
        </li>
      </ul>

      {/* 🔹 Section 5 — Semantic Elements */}
      <Section
        title="5️⃣ Semantic Elements"
        html={`<header>
  <h1>Welcome to My Website</h1>
</header>
<nav>
  <a href="#">Home</a> | <a href="#">About</a> | <a href="#">Contact</a>
</nav>
<main>
  <article>
    <h2>Article Title</h2>
    <p>This is an article section.</p>
  </article>
</main>
<footer>© 2025 My Website</footer>`}
        description="Semantic elements clearly describe their meaning to browsers and developers, improving accessibility and SEO."
      />

      <ul className="tutorial-list-points">
        <li>
          <code>&lt;header&gt;</code> — introductory section
        </li>
        <li>
          <code>&lt;nav&gt;</code> — navigation links
        </li>
        <li>
          <code>&lt;main&gt;</code> — main content
        </li>
        <li>
          <code>&lt;article&gt;</code> / <code>&lt;section&gt;</code> — grouped
          content
        </li>
        <li>
          <code>&lt;footer&gt;</code> — bottom area or credits
        </li>
      </ul>

      {/* 🔹 Section 6 — Example: All Together */}
      <Section
        title="6️⃣ Complete Example"
        html={`<!DOCTYPE html>
<html>
  <head>
    <title>HTML Elements Example</title>
  </head>
  <body>
    <header><h1>My Website</h1></header>
    <main>
      <article>
        <h2>Welcome!</h2>
        <p>This is a <strong>simple</strong> webpage example using HTML elements.</p>
        <img src="https://via.placeholder.com/120" alt="Demo" />
      </article>
    </main>
    <footer>© 2025 My Website</footer>
  </body>
</html>`}
        description="A real-world example combining structure, text, and semantic tags into one HTML document."
      />

      {/* 🔹 Summary */}
      <div
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
          padding: "1rem",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2>📘 Summary</h2>
        <ul className="tutorial-list-points">
          <li>HTML elements are the foundation of every web page.</li>
          <li>Each element has an opening tag, content, and closing tag.</li>
          <li>Some elements (like img, br) are self-closing.</li>
          <li>
            Understand the difference between block and inline elements for
            layout control.
          </li>
          <li>Use semantic elements for better accessibility and SEO.</li>
        </ul>
      </div>
    </div>
  );
}
